/**
 * Domínio: regras de manutenção de contatos pelo admin — C-75 (ajuste de 14/10)
 * + GUPM-202 (contato único por pessoa).
 * TS puro. As travas do mínimo de emergência têm guarda espelhada no banco
 * (trigger `contacts_guarda_desativacao`, C7501), para a API direta não furar a regra.
 */
import { CONTATO_SUPORTE_APP_ID } from '@/domain/suporte';
import { normalizarFuncoes, validarContato, type ContatoEntrada, type ContatoGravado, type UsuarioVinculado } from './validacao';

/** Decisão do Gabriel (14/10): a lista de emergência nunca fica com menos de 2 ativos. */
export const MINIMO_EMERGENCIA_ATIVOS = 2;

export interface ContatoComEstado {
  id: string;
  is_emergencia: boolean;
  is_active: boolean;
}

const MSG_MINIMO = `É preciso manter ao menos ${MINIMO_EMERGENCIA_ATIVOS} contatos de emergência ativos.`;

/** O contato está entre os últimos `MINIMO_EMERGENCIA_ATIVOS` de emergência ativos? */
function seguraOMinimo(contato: ContatoComEstado, todos: readonly ContatoComEstado[]): boolean {
  if (!contato.is_emergencia || !contato.is_active) return false;
  return todos.filter((c) => c.is_emergencia && c.is_active).length <= MINIMO_EMERGENCIA_ATIVOS;
}

/** Por que o contato não pode ser desativado agora, ou `null` se pode. */
export function motivoBloqueioDesativar(contato: ContatoComEstado, todos: readonly ContatoComEstado[]): string | null {
  if (contato.id === CONTATO_SUPORTE_APP_ID) {
    return 'Este contato fornece o e-mail de suporte mostrado na tela de falha de carga. Pode ser editado, mas não desativado.';
  }
  if (seguraOMinimo(contato, todos)) return `${MSG_MINIMO} Cadastre ou ative outro antes de desativar este.`;
  return null;
}

/** GUPM-202 — por que a marcação "É de emergência" não pode ser tirada agora, ou `null`. */
export function motivoBloqueioDesmarcarEmergencia(
  contato: ContatoComEstado,
  todos: readonly ContatoComEstado[],
): string | null {
  return seguraOMinimo(contato, todos) ? `${MSG_MINIMO} Marque outro antes de desmarcar este.` : null;
}

/** `sort_order` de um contato novo: fim da lista, de 10 em 10. */
export function proximaOrdem(todos: readonly { sort_order: number }[]): number {
  return todos.length ? Math.max(...todos.map((c) => c.sort_order)) + 10 : 0;
}

/** Sugestões do campo Função: as funções já usadas, sem repetição, em ordem alfabética. */
export function sugestoesDeFuncao(contatos: readonly { funcoes: readonly string[] }[]): string[] {
  return normalizarFuncoes(contatos.flatMap((c) => c.funcoes)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/**
 * Usuários do app que o formulário oferece: os que não estão vinculados a OUTRO
 * contato (um usuário, um contato — índice único no banco). O do próprio contato
 * continua na lista.
 */
export function usuariosVinculaveis<U extends { id: string }>(
  usuarios: readonly U[],
  contatos: readonly { id: string; app_user_id: string | null }[],
  contatoId: string | null,
): U[] {
  const ocupados = new Set(contatos.filter((c) => c.id !== contatoId && c.app_user_id).map((c) => c.app_user_id));
  return usuarios.filter((u) => !ocupados.has(u.id));
}

/**
 * O formulário mudou algo em relação ao gravado? Compara pelo que IRIA ao banco
 * (normalizado, com trim e vazio = null), para que espaço a mais não habilite o
 * "Salvar alterações". Com a entrada inválida, compara campo a campo com trim.
 */
export function houveAlteracao(entrada: ContatoEntrada, gravado: ContatoGravado, usuario?: UsuarioVinculado | null): boolean {
  const r = validarContato(entrada, { gravado, usuario });
  const atual = r.ok ? r.valor : { ...entrada, funcoes: normalizarFuncoes(entrada.funcoes) };
  const campos = ['name', 'phone', 'display', 'email', 'empresa', 'disponibilidade'] as const;
  // Vazio e nulo valem o mesmo dos dois lados (telefone pendente é '', e-mail vazio é null).
  const norm = (v: string | null) => (v ?? '').trim();
  return (
    campos.some((k) => norm(atual[k]) !== norm(gravado[k])) ||
    atual.funcoes.join('\n') !== gravado.funcoes.join('\n') ||
    atual.is_emergencia !== gravado.is_emergencia ||
    atual.app_user_id !== gravado.app_user_id
  );
}
